import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';

import { cn } from '@rsc/tailwind-base/utils';
import { Button } from '@rsc/ui-components/shadcn/Button';
import Image from '@rsc/ui-components/tailwind/Image';
import ImageZoomOnHover from '@rsc/ui-components/tailwind/ImageZoomOnHover';
import { shrinkProductImage } from '@rsc/utils';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import {
  CarouselProps,
  ImageProps,
  SelectedMedia,
  useCarouselDefaultProps,
} from './props.types';

export const DesktopImageCarousel = (props: Readonly<CarouselProps>) => {
  const { tt } = useUiComponentsInternationalContext();

  const CarouselDefaultProps = useCarouselDefaultProps();

  const images = props?.images ?? CarouselDefaultProps.images;
  const selectedMedia =
    props?.selectedMedia ?? CarouselDefaultProps.selectedMedia;
  const setSelectedMedia =
    props?.setSelectedMedia ?? CarouselDefaultProps.setSelectedMedia;

  const imgWrapRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handlePrevious = () => {
    const currentIndex = images.findIndex(
      (m: ImageProps) => m.id === selectedMedia?.id
    );
    const previousIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedMedia(images[previousIndex] as SelectedMedia);
  };

  const handleNext = () => {
    const currentIndex = images.findIndex(
      (m: ImageProps) => m.id === selectedMedia?.id
    );
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedMedia(images[nextIndex] as SelectedMedia);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(true);
    startXRef.current = event.clientX;
    scrollLeftRef.current = containerRef?.current?.scrollLeft || 0;
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    if (!isDragging) return;
    event.preventDefault();
    const x = event.clientX;
    const scroll = x - startXRef.current;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeftRef.current - scroll;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getAspectRefHeight = (): number => {
    if (imgWrapRef.current) {
      const { height } = imgWrapRef.current.getBoundingClientRect();
      return height;
    }
    return 400;
  };

  return (
    <div className="hidden w-full relative lg:block">
      <div className="flex items-center">
        <div className="aspect-video w-full overflow-hidden" ref={imgWrapRef}>
          <AnimatePresence>
            <motion.div
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              initial={{ x: 0, opacity: 0 }}
              key={selectedMedia?.image?.src}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-center">
                {selectedMedia?.image?.src && (
                  <ImageZoomOnHover
                    src={selectedMedia?.image?.src}
                    alt={
                      selectedMedia?.image?.altText ||
                      tt(s => s.productContainerDeptComponents.productImageAlt)
                    }
                    imageHeight={getAspectRefHeight()}
                    fetchPriority={
                      selectedMedia.id === images[0]?.id ? 'high' : 'low'
                    }
                  />
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <Button
          aria-label={tt(
            s => s.productContainerDeptComponents.previousAriaLabel
          )}
          className="absolute left-4 top-[40%] rounded-full"
          onClick={handlePrevious}
          size="icon"
          variant="cta"
        >
          <MdArrowBack size={24} />
        </Button>
        <Button
          aria-label={tt(s => s.productContainerDeptComponents.nextAriaLabel)}
          className="absolute right-4 top-[40%] rounded-full"
          onClick={handleNext}
          size="icon"
          variant="cta"
        >
          <MdArrowForward size={24} />
        </Button>
      </div>
      <div className="w-full relative">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-transparent to-background via-80% pointer-events-none z-[1]" />
        <div
          className="overflow-auto w-full pr-24"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          ref={containerRef}
          role="button"
          tabIndex={0}
        >
          <div className="hidden cursor-auto flex-nowrap gap-4 w-max h-40 py-10 lg:flex">
            {images.map((m, ix) => (
              <Button
                className="h-full w-auto p-0 aspect-square"
                key={m.id}
                onClick={() => setSelectedMedia(m)}
              >
                {/* { Keep image size same as desktop thumbnail size in ImageZoomOnHover, so it only loads 1 set of images } */}
                <Image
                  alt={
                    m.image.altText ||
                    `${tt(
                      s => s.productContainerDeptComponents.placeholderImageAlt
                    )} ${ix}`
                  }
                  className={cn(
                    'cursor-pointer rounded-lg border hover:scale-105 transition ease-in-out delay-100',
                    selectedMedia?.id === m.id ? 'border-ring' : 'border-border'
                  )}
                  src={shrinkProductImage(m.image.src, 600)}
                  width="5rem"
                />
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
